import omit from 'lodash/omit'
import partition from 'lodash/partition'
import { dayjsUtc } from 'lib/dayjs'
import { useEffect } from 'react'
import * as Sentry from '@sentry/react'

import { RESOURCES, useFetchArrayResource } from 'api'
import { AircallEvent } from 'lib/models/aircall-event'
import {
  EIGHT_X_EIGHT_REQUEST_TIMEOUT_MS,
  EightxEightEvent,
} from 'lib/models/eight-x-eight-event'
import { Comment, CommentAttributes } from 'lib/models/comment'
import { Event } from 'lib/models/event'
import { ExternalEmail } from 'lib/models/external-email'
import { Task } from 'lib/models/task'
import { PRODUCTS } from 'utils/enums'
import { Response, flattenAttributes } from 'utils/helpers'

type GroupedComments = {
  [key: string]: Response[]
}

export type Activity = {
  attributes: object
  id?: number | string
  type?: string
}

export const isEdited = (
  createdAt: string,
  updatedAt: string
): { edited: boolean; lastInteractedWith: string } => {
  const edited = updatedAt > createdAt

  return {
    edited,
    lastInteractedWith: edited ? updatedAt : createdAt,
  }
}

const getActivityDate = (activity: Activity): string | null => {
  if (activity.type === 'tasks')
    return (activity as Task).attributes.completedAt

  if (activity.type === 'aircall')
    // convert the Unix timestamp to an ISO string
    return new Date(
      (activity as AircallEvent).attributes.started_at * 1000
    ).toISOString()

  if (activity.type === '8x8')
    return (activity as EightxEightEvent).attributes.createdAt

  if (activity.type === 'external_emails')
    return (activity as unknown as ExternalEmail).attributes.emailedAt

  if ((activity as Comment).attributes?.replies?.length) {
    const [lastReply] = (activity as Comment).attributes.replies.slice(-1)
    return lastReply.createdAt
  }

  return (activity as { attributes: { createdAt: string } }).attributes
    .createdAt
}

export const sortByActivityDate = (a: Activity, b: Activity): 1 | -1 => {
  return dayjsUtc(getActivityDate(a)).isBefore(dayjsUtc(getActivityDate(b)))
    ? 1
    : -1
}

export const useEvents = ({
  product,
  params,
}: {
  product: string
  params: string
}): Event[] => {
  const { data: events, refetch: refetchEvents } = useFetchArrayResource(
    {
      resource: RESOURCES.EVENTS,
      params,
    },
    { enabled: false, initialData: { data: [] } }
  )

  useEffect(() => {
    if (product === PRODUCTS.FUNERAL_PLAN) {
      refetchEvents()
    }
  }, [product, refetchEvents])

  return events?.data as Event[]
}

export const useProbateCaseEvents = ({
  product,
  productId,
}: {
  product: string
  productId: string
}): Event[] => {
  const { data: events, refetch: refetchEvents } = useFetchArrayResource<Event>(
    {
      resource: RESOURCES.EVENTS,
      params: `probateCaseId=${productId}`,
    },
    {
      enabled: product === PRODUCTS.PROBATE && !!productId,
      initialData: { data: [] },
    }
  )

  useEffect(() => {
    if (product === PRODUCTS.PROBATE) {
      refetchEvents()
    }
  }, [product, refetchEvents])

  const probateCaseEvents = events?.data.map((event) => {
    const isDocumentEvent = event.attributes.name.includes('document')
    if (!isDocumentEvent) return event
    const documentWasDeleted = events.data.find(
      (e) =>
        e.attributes.name === 'probate_document_deleted' &&
        e.attributes.metadata.documentId ===
          event.attributes.metadata.documentId
    )
    return {
      ...event,
      attributes: {
        ...event.attributes,
        metadata: {
          ...event.attributes.metadata,
          isDeleted: !!documentWasDeleted,
        },
      },
    }
  })

  return probateCaseEvents as Event[]
}

const isNewReply = ({
  attributes,
}: {
  attributes: CommentAttributes
}): boolean => !!attributes.parentId

export const getCaseCommentThreads = ({
  groupedComments,
  isCall,
}: {
  groupedComments: GroupedComments
  isCall: boolean
}): { attributes: object }[] => {
  const [newReplies, comments] = partition(groupedComments.null, isNewReply)

  const filteredComments = (comments as Comment[]).filter(
    (comment) => comment.attributes.isCall === isCall
  )

  return filteredComments.map((comment) => {
    const replies = [
      ...comment.attributes.replies,
      ...(newReplies as Response[])
        .filter(
          ({ attributes }) =>
            'parentId' in attributes &&
            attributes.parentId === comment.id.toString()
        )
        .map(flattenAttributes),
    ]

    return {
      ...comment,
      attributes: { ...comment.attributes, replies },
    }
  })
}

export const getTaskCommentThreads = (
  groupedComments: GroupedComments
): { attributes: object }[] => {
  return Object.values(omit(groupedComments, 'null')).map((group) => {
    const parent = group[0]
    const replies = group.slice(1).map(flattenAttributes)

    return {
      ...parent,
      attributes: { ...parent.attributes, replies },
    }
  })
}

// A request that "times out" long after the timeout almost always means the
// browser was suspended (e.g. a laptop asleep with the tab open), not a slow 8x8.
const LIKELY_SUSPENDED_GRACE_MS = 5_000

export const report8x8FetchError = (
  error: unknown,
  {
    leadId,
    contactId,
    startedAt,
  }: { leadId?: number | null; contactId?: number | null; startedAt: number }
): void => {
  const durationMs = Date.now() - startedAt

  Sentry.captureException(error, {
    tags: {
      'eight_x_eight.likely_suspended':
        durationMs >
        EIGHT_X_EIGHT_REQUEST_TIMEOUT_MS + LIKELY_SUSPENDED_GRACE_MS,
    },
    contexts: {
      eight_x_eight_request: {
        leadId,
        contactId,
        timeoutMs: EIGHT_X_EIGHT_REQUEST_TIMEOUT_MS,
        durationMs,
      },
    },
  })
}
